<template>
  <v-card border>
    <v-card-text>
      <pre v-if="documentRequestError && !$loading.isLoading('init')">
        {{ documentRequestError }}
      </pre>
      <b-section-loader
        v-if="!documentRequestError"
        :loading="$loading.isLoading('init')">
        <div class="d-flex align-center justify-space-between ga-3 mb-3">
          <div class="d-flex align-center ga-3">
            <div class="icon--blue">
              <v-icon icon="mdi-file-document-outline" size="23"></v-icon>
            </div>

            <div>
              <div class="font-weight-bold font-size-18">
                {{ documentSource.definition?.name }}
                {{ documentSource.docTypeClass.name }}
              </div>
              <div class="d-flex align-center ga-3">
                <span class="text-disabled">
                  <span v-if="activeVersion" class="text-disabled">
                    Версія {{ activeVersion.version }} |
                  </span>
                  {{ new CustomDate(documentSource.created_at).toString() }}
                </span>
                <v-chip
                  :color="getStatusColor(documentSource.status)"
                  rounded="pill"
                  density="compact">
                  <b>{{
                    keyToLabel(documentSource.status, 'documentStatus')
                  }}</b>
                </v-chip>
              </div>
            </div>
          </div>
          <div class="d-flex align-center ga-2">
            <v-btn
              v-if="documentSource.createdBy === userId"
              border
              :disabled="disabled"
              density="comfortable"
              icon
              variant="flat"
              color="white"
              @click="
                () =>
                  selectFile({ multiple: false })
                    .then(e =>
                      uploadFiles({
                        files: e.blob,
                        mainFolder: 'Документы',
                        nameFolder: documentUuid,
                      })
                    )
                    .then(({ files }) => (documentCredentials.document = files))
                    .then(updateDocumentFile)
              ">
              <v-icon icon="intime:pencil" color="grey-darken-3"></v-icon>
            </v-btn>
            <v-btn
              v-if="!acceptanceStatus.isInAcceptanceList"
              border
              density="comfortable"
              icon
              variant="flat"
              color="white">
              <v-icon icon="intime:copy" color="grey-darken-3"></v-icon>
            </v-btn>
            <v-btn
              border
              density="comfortable"
              icon
              variant="flat"
              color="white">
              <v-icon icon="mdi-tray-arrow-down" color="grey-darken-3"></v-icon>
            </v-btn>
            <v-btn
              border
              density="comfortable"
              icon
              variant="flat"
              color="white">
              <v-icon icon="mdi-printer-outline" color="grey-darken-3"></v-icon>
            </v-btn>
            <v-btn
              v-if="!acceptanceStatus.isInAcceptanceList"
              border
              density="comfortable"
              icon
              variant="flat"
              color="white">
              <v-icon icon="intime:archive" color="grey-darken-3"></v-icon>
            </v-btn>
            <v-btn
              v-tooltip="'Електронні підписи'"
              border
              density="comfortable"
              icon
              :variant="signatures.length ? 'outlined' : 'flat'"
              :class="{ 'bg-blue-lighten-5': signatures.length }"
              :color="signatures.length ? 'light-blue-darken-4' : 'white'"
              @click="showSignatures">
              <v-icon
                icon="intime:key"
                :color="
                  signatures.length ? 'light-blue-darken-4' : 'grey-darken-3'
                "></v-icon>
            </v-btn>
            <v-btn
              v-if="acceptanceStatus.isInAcceptanceList"
              v-tooltip="'Учасники ознайомлення'"
              border
              density="comfortable"
              icon
              variant="flat"
              color="white"
              @click="showAcceptanceParticipants">
              <v-icon
                icon="intime:account-multiple"
                color="grey-darken-3"></v-icon>
            </v-btn>
            <v-btn
              v-if="documentSource.createdBy === userId"
              v-tooltip="'Видалити документ'"
              density="comfortable"
              :disabled="documentSource.status !== 'draft'"
              :loading="$loading.isLoading('documentDelete')"
              icon="intime:delete"
              color="error"
              variant="outlined"
              @click="deleteDocument"></v-btn>
            <v-btn
              v-if="
                acceptanceStatus.isInAcceptanceList &&
                !acceptanceStatus.isConfirmed
              "
              :loading="
                $loading.isLoading('documentAcceptanceConfirmationCreate')
              "
              @click="acceptanceConfirmationCreate">
              <v-icon
                :icon="
                  acceptanceStatus.acceptanceType?.id === 2
                    ? 'mdi-draw-pen'
                    : 'mdi-check'
                "
                class="mr-2"></v-icon>
              <span>
                {{
                  acceptanceStatus.acceptanceType?.id === 2
                    ? 'Підписати та ознайомитись'
                    : 'Ознайомитись'
                }}
              </span>
            </v-btn>
            <v-btn
              v-if="
                acceptanceStatus.isInAcceptanceList &&
                acceptanceStatus.isConfirmed
              "
              color="success"
              :loading="
                $loading.isLoading('documentAcceptanceConfirmationCreate')
              "
              readonly>
              <v-icon icon="mdi-check" class="mr-2"></v-icon>
              <span>Ознайомлений</span>
            </v-btn>
            <b-action-menu v-if="actions.length" :actions="actions">
              <template #activator="{ props }">
                <v-btn v-bind="props" density="default">
                  <v-icon icon="mdi-dots-horizontal" class="mr-2"></v-icon>
                  <span>Дії</span>
                </v-btn>
              </template>
            </b-action-menu>
          </div>
        </div>

        <tabs :allows-routes="allowsRoutes" class="mb-5"></tabs>

        <router-view></router-view>
      </b-section-loader>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import Tabs from './components/Tabs.vue'
import { BActionMenu } from 'best-modules/components'
import { initDocument } from './document'
import { BSectionLoader } from 'best-modules/components'
import { CustomDate } from 'best-modules/utils'
import { computed } from 'vue'
import { useBreadcrumb, useStore } from '@/plugins'
import { ActionListItem } from 'best-modules/components/action-list/types'
import { handleAsync } from 'best-modules/plugins'
import { fillDirectory } from '@/directory'
import { initComments } from '@/components/comments/v2'
import { keyToLabel } from '@/utils/key-to-label'
import { getStatusColor } from '@/pages/document/helpers'
import { selectFile } from '@/components/filesUploader/selectFile'
import { uploadFiles } from '@/components/filesUploader/uploadFiles'
import { capitalize } from 'lodash'

defineOptions({
  name: 'DocumentSingle',
})
const store = useStore()
const userId = computed(() => store.state.user.userData?.id)
const breadcrumb = useBreadcrumb()
const {
  documentId,
  documentUuid,
  documentSource,
  documentCredentials,
  documentRequestError,
  routeRun,
  agree,
  disabled,
  currentUserParticipant,
  currentStage,
  isEcpRequired,
  activeRoute,
  allowsRoutes,
  allowsSendToReview,
  sendToRegistration,
  sendToReview,
  myReview,
  getMyReview,
  deleteDocument,
  revision,
  activeVersion,
  acceptanceStatus,
  acceptanceConfirmationCreate,
  getDocument,
  updateDocumentFile,
  getActiveRoute,
  getDocumentSignatureList,
  signatures,
  getAcceptanceList,
  getAcceptanceStatus,
  getAcceptanceConfirmationList,
  getAttachmentList,
  getRelationList,
  getVersionList,
  showSignatures,
  showAcceptanceParticipants,
} = initDocument()
initComments({
  entityId: documentId,
  entityTypeId: 11,
})

handleAsync('init', async () => {
  await fillDirectory('docTypeFlowTypes')
  await getDocument()
  await getActiveRoute()
  await getDocumentSignatureList()
  await getMyReview()
  await getAcceptanceStatus()
  await getAcceptanceList()
  await getAcceptanceConfirmationList()
  await getAttachmentList()
  await getRelationList()
  await getVersionList()

  breadcrumb.set([
    {
      index: 0,
      title: `${documentSource.docTypeClass.name} ${documentSource.definition.name}`,
      disabled: true,
    },
  ])
})

const canSendToRegistration = computed<boolean>(() => {
  return (
    documentSource.status === 'in_progress' &&
    documentSource.registrationTypeId === 2 &&
    !currentStage.value &&
    !!activeRoute.value?.stages.length
  )
})

const actions = computed<ActionListItem[]>(() => {
  const authorActions: ActionListItem[] = [
    {
      title: 'Відправити на погодження',
      action: () => routeRun(),
      disabled: !['draft', 'revision'].includes(documentSource.status),
      icon: 'mdi-send-outline',
      iconProps: {
        color: 'info',
      },
    },
  ]

  const registrationActions: ActionListItem[] = [
    {
      title: 'Відправити на реєстрацію',
      action: () => sendToRegistration(),
      disabled: !canSendToRegistration.value,
      icon: 'mdi-text-box-edit-outline',
      iconProps: {
        color: 'purple',
      },
    },
  ]

  const incomingActions: ActionListItem[] = [
    {
      title: 'Відправити на реєстрацію',
      action: () => sendToRegistration(),
      disabled: documentSource.status !== 'draft',
      icon: 'mdi-text-box-edit-outline',
      iconProps: {
        color: 'purple',
      },
    },
    {
      title: 'Передати на розгляд',
      action: () => sendToReview(),
      disabled: documentSource.status !== 'registered',
      icon: 'mdi-account-arrow-right-outline',
      iconProps: {
        color: 'info',
      },
    },
  ]

  const reviewerActions: ActionListItem[] = [
    {
      title: 'Передати на виконання',
      action: () => {
        /* TODO: діалог створення резолюції */
      },
      disabled: documentSource.status !== 'under_review',
      icon: 'mdi-send-outline',
      iconProps: {
        color: 'success',
      },
    },
    {
      title: 'Закрити розгляд',
      action: () => {
        /* TODO: діалог закриття розгляду */
      },
      disabled: documentSource.status !== 'under_review',
      icon: 'mdi-check-circle-outline',
      iconProps: {
        color: 'grey',
      },
    },
    {
      title: 'Змінити розглядаючого',
      action: () => {
        /* TODO: діалог зміни розглядаючого */
      },
      disabled: documentSource.status !== 'under_review',
      icon: 'mdi-account-switch-outline',
      iconProps: {
        color: 'warning',
      },
    },
    {
      title: 'Повернути реєстратору',
      action: () => {
        /* TODO: діалог повернення */
      },
      disabled: documentSource.status !== 'under_review',
      icon: 'mdi-arrow-u-left-top',
      iconProps: {
        color: 'error',
      },
    },
  ]

  let participantTitle: string =
    currentStage.value?.typeId === 2 ? 'затвердити' : 'погодити'
  let participantIcon: ActionListItem['icon'] = 'mdi-check'
  if (isEcpRequired.value) {
    participantTitle = `підписати та ${participantTitle}`
    participantIcon = 'mdi-draw-pen'
  }

  const participantActions: ActionListItem[] = [
    {
      title: capitalize(participantTitle),
      action: () => agree(1),
      icon: participantIcon,
      iconProps: {
        color: 'success',
      },
      disabled: documentSource.status !== 'in_progress',
    },
    {
      title: 'Відхилити',
      action: () => agree(2),
      icon: 'mdi-close',
      iconProps: {
        color: 'error',
      },
      disabled: documentSource.status !== 'in_progress',
    },
    {
      title: 'Повернути на доопрацювання',
      action: () => revision(),
      icon: 'mdi-rotate-left',
      iconProps: {
        color: 'warning',
      },
      disabled: documentSource.status !== 'in_progress',
    },
  ]

  const actions: ActionListItem[] = []
  if (documentSource.createdBy === userId.value && allowsRoutes.value) {
    actions.push(...authorActions)
  }
  if (
    documentSource.createdBy === userId.value &&
    allowsRoutes.value &&
    canSendToRegistration.value
  ) {
    actions.push(...registrationActions)
  }
  if (documentSource.createdBy === userId.value && allowsSendToReview.value) {
    actions.push(...incomingActions)
  }
  if (allowsSendToReview.value && !!myReview.value) {
    actions.push(...reviewerActions)
  }
  if (
    currentUserParticipant.value &&
    !currentUserParticipant.value.resultId &&
    currentStage.value?.participants.some(
      p => p.id === currentUserParticipant.value?.id
    )
  ) {
    actions.push(...participantActions)
  }

  return actions
})
</script>
